interface Topic {
  id: string;
  name: string;
}

interface AboutTabProps {
  description: string;
  rules: string[];
  topics: Topic[];
  createdAt: string;
  stats: {
    totalPosts: number;
    totalMembers: number;
    activeToday: number;
  };
  isOwnerOrAdmin?: boolean;
  onEditRules?: () => void;
}

const AboutTab = ({ description, rules, topics, createdAt, stats, isOwnerOrAdmin = false, onEditRules }: AboutTabProps) => {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">About This Group</h2>
        <p className="text-gray-700 leading-relaxed">{description}</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Group Rules</h2>
          {isOwnerOrAdmin && onEditRules && (
            <button
              onClick={onEditRules}
              className="flex items-center gap-1.5 px-3 py-1.5 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line"></i>
              Edit Rules
            </button>
          )}
        </div>
        {rules.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-200">
            <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-2">
              <i className="ri-shield-check-line text-lg text-teal-500"></i>
            </div>
            <p className="text-gray-500 text-sm mb-1">No rules have been set for this group yet.</p>
            {isOwnerOrAdmin && onEditRules && (
              <button
                onClick={onEditRules}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap mt-1"
              >
                + Add group rules
              </button>
            )}
          </div>
        ) : (
          <ol className="space-y-3">
            {rules.map((rule, index) => (
              <li key={index} className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-sm font-semibold">
                  {index + 1}
                </span>
                <span className="text-gray-700 flex-1 leading-relaxed pt-0.5">{rule}</span>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Topics</h2>
        {topics.length === 0 ? (
          <p className="text-gray-500">No topics have been added yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {topics.map((topic) => (
              <span
                key={topic.id}
                className="px-3 py-1.5 bg-teal-50 text-teal-700 rounded-full text-sm font-medium hover:bg-teal-100 transition-colors cursor-pointer"
              >
                #{topic.name}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Activity Stats</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-teal-600 mb-1">{stats.totalPosts}</div>
            <div className="text-sm text-gray-600">Total Posts</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-teal-600 mb-1">{stats.totalMembers}</div>
            <div className="text-sm text-gray-600">Total Members</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-teal-600 mb-1">{stats.activeToday}</div>
            <div className="text-sm text-gray-600">Active Today</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Group Info</h2>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <i className="ri-calendar-line text-lg"></i>
            <span>Created on {new Date(createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutTab;